import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Quote } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../../components/ImageUpload';
import ImagePickerDialog from './ImagePickerDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PriestBlogsManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [quote, setQuote] = useState({ text: { sv: '' }, signature: 'Kyrkoherden' });
  const [editingQuote, setEditingQuote] = useState(false);
  
  const [formData, setFormData] = useState({
    title: { sv: '' },
    content: { sv: '' },
    excerpt: { sv: '' },
    image_url: '',
    author: 'Kyrkoherden',
    status: 'draft'
  });

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/priest-blogs`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setBlogs(data);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Kunde inte hämta blogginlägg');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuote = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/priest-quote`);
      if (res.ok) {
        const data = await res.json();
        setQuote(data);
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
    fetchQuote();
  }, [fetchBlogs, fetchQuote]);

  const handleSaveQuote = async () => {
    try {
      const res = await fetch(`${API_URL}/api/priest-quote`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quote)
      });
      
      if (res.ok) {
        toast.success('Citat sparat och översatt!');
        setEditingQuote(false);
        fetchQuote();
      } else {
        toast.error('Kunde inte spara citatet');
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Ett fel uppstod');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.sv.trim()) {
      toast.error('Titel krävs');
      return;
    }
    
    try {
      const url = editingBlog 
        ? `${API_URL}/api/priest-blogs/${editingBlog.id}`
        : `${API_URL}/api/priest-blogs`;
      
      const res = await fetch(url, {
        method: editingBlog ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingBlog ? 'Inlägg uppdaterat!' : 'Inlägg skapat och översatt!');
        resetForm();
        fetchBlogs();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Något gick fel');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort detta inlägg?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/priest-blogs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        toast.success('Inlägg borttaget');
        fetchBlogs();
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Kunde inte ta bort inlägget');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || { sv: '' },
      content: blog.content || { sv: '' },
      excerpt: blog.excerpt || { sv: '' },
      image_url: blog.image_url || '',
      author: blog.author || 'Kyrkoherden',
      status: blog.status || 'draft'
    });
    setIsCreating(true);
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '' },
      content: { sv: '' },
      excerpt: { sv: '' },
      image_url: '',
      author: 'Kyrkoherden',
      status: 'draft'
    });
    setEditingBlog(null);
    setIsCreating(false);
  };

  const toggleStatus = async (blog) => {
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      const res = await fetch(`${API_URL}/api/priest-blogs/${blog.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...blog, status: newStatus })
      });
      
      if (res.ok) {
        toast.success(newStatus === 'published' ? 'Inlägg publicerat!' : 'Inlägg avpublicerat');
        fetchBlogs();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  if (loading) {
    return <div className="p-4">Laddar...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Prästens lilla hörna</h2>
        {!isCreating && (
          <Button onClick={() => setIsCreating(true)} data-testid="create-blog-button">
            <Plus className="w-4 h-4 mr-2" />
            Nytt inlägg
          </Button>
        )}
      </div>

      {/* Priest Quote Section */}
      <Card className="bg-yellow-50 border-yellow-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Quote className="w-5 h-5 text-yellow-600" />
            Prästens citat (visas överst på sidan)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editingQuote ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Citat (svenska)</label>
                <textarea
                  value={quote.text?.sv || ''}
                  onChange={(e) => setQuote({ ...quote, text: { ...quote.text, sv: e.target.value } })}
                  className="w-full border rounded-lg p-3 min-h-[100px]"
                  placeholder="Skriv prästens citat här..."
                  data-testid="quote-text-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signatur</label>
                <Input
                  value={quote.signature || ''}
                  onChange={(e) => setQuote({ ...quote, signature: e.target.value })}
                  placeholder="Kyrkoherden"
                  data-testid="quote-signature-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveQuote} className="bg-yellow-600 hover:bg-yellow-700" data-testid="save-quote-button">
                  <Save className="w-4 h-4 mr-2" />
                  Spara & översätt
                </Button>
                <Button variant="outline" onClick={() => setEditingQuote(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Avbryt
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-800 italic mb-2">"{quote.text?.sv || 'Inget citat ännu'}"</p>
              <p className="text-gray-600 text-sm mb-3">— {quote.signature || 'Kyrkoherden'}</p>
              <Button variant="outline" size="sm" onClick={() => setEditingQuote(true)} data-testid="edit-quote-button">
                <Edit className="w-4 h-4 mr-2" />
                Redigera citat
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Form */}
      {isCreating && (
        <Card>
          <CardHeader>
            <CardTitle>{editingBlog ? 'Redigera inlägg' : 'Skapa nytt inlägg'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel (svenska) *</label>
                <Input
                  value={formData.title.sv}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, sv: e.target.value } })}
                  placeholder="Skriv titel..."
                  required
                  data-testid="blog-title-input"
                />
                <p className="text-xs text-gray-500 mt-1">Översätts automatiskt till andra språk</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sammanfattning (svenska)</label>
                <textarea
                  value={formData.excerpt.sv}
                  onChange={(e) => setFormData({ ...formData, excerpt: { ...formData.excerpt, sv: e.target.value } })}
                  className="w-full border rounded-lg p-3 min-h-[60px]"
                  placeholder="Kort sammanfattning som visas i listningen..."
                  data-testid="blog-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Innehåll (svenska) *</label>
                <textarea
                  value={formData.content.sv}
                  onChange={(e) => setFormData({ ...formData, content: { ...formData.content, sv: e.target.value } })}
                  className="w-full border rounded-lg p-3 min-h-[200px]"
                  placeholder="Skriv inläggets innehåll..."
                  required
                  data-testid="blog-content-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bild</label>
                <ImageUpload
                  onUpload={(url) => setFormData({ ...formData, image_url: url })}
                  currentImage={formData.image_url}
                />
                <div className="flex items-center gap-2 mt-2">
                  <ImagePickerDialog 
                    currentImage={formData.image_url}
                    onSelect={(url) => setFormData({ ...formData, image_url: url })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Författare</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Kyrkoherden"
                    data-testid="blog-author-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border rounded-lg p-2"
                    data-testid="blog-status-select"
                  >
                    <option value="draft">Utkast</option>
                    <option value="published">Publicerad</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" data-testid="save-blog-button">
                  <Save className="w-4 h-4 mr-2" />
                  {editingBlog ? 'Uppdatera' : 'Skapa & översätt'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Avbryt
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Blog List */}
      <Card>
        <CardHeader>
          <CardTitle>Blogginlägg ({blogs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {blogs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Inga inlägg ännu. Skapa ditt första inlägg!</p>
          ) : (
            <div className="space-y-3">
              {blogs.map((blog) => (
                <div 
                  key={blog.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`blog-item-${blog.id}`}
                >
                  <div className="flex items-center gap-4">
                    {blog.image_url && (
                      <img 
                        src={blog.image_url.startsWith('http') ? blog.image_url : `${API_URL}${blog.image_url}`}
                        alt={blog.title?.sv || ''}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div>
                      <h4 className="font-medium text-gray-900">{blog.title?.sv || 'Utan titel'}</h4>
                      <p className="text-sm text-gray-500">
                        {blog.author} • {blog.published_at ? new Date(blog.published_at).toLocaleDateString('sv-SE') : 'Ej publicerad'}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded ${blog.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                        {blog.status === 'published' ? 'Publicerad' : 'Utkast'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => toggleStatus(blog)}
                      title={blog.status === 'published' ? 'Avpublicera' : 'Publicera'}
                    >
                      {blog.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(blog)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(blog.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PriestBlogsManager;
